import type { NamedFieldProps } from "./Field.js";
import type { Data, ImmutableDictionary } from "shelving";
import { DataSchema, DictionarySchema, assertData, assertDictionary, getProps, getSource, getString, omitProps, withProp } from "shelving";
import { useStore } from "shelving/react";
import { useDialog } from "../base/Dialogs.js";
import { Field, useField } from "./Field.js";
import { FieldNotice } from "./FieldNotice.js";
import { Form } from "./Form.js";
import { SubmitButton } from "./SubmitButton.js";

/**
 * Re-key a dictionary to use only `01` - `99` string keys.
 *
 * It's usually preferable to use maps/dictionaries (rather than arrays) to store lists of data.
 * Maps allow data to be referenced uniquely when and values can be deeply merged in.
 *
 * However: Firestore stores map objects alphabetically, so maps with randomly generated IDs would not not retrieved in the same order they're stored in.
 * This method rekeys the object using numerically indexed keys to preserve initial ordering.
 */
function _rekeyDictionary<T>(obj: ImmutableDictionary<T>): ImmutableDictionary<T> {
	return Object.fromEntries(Object.values(obj).map((v, i) => [i.toString().padStart(2, "0"), v]));
}

/** Component representing an <input> element. */
export function RepeaterField<T extends Data>({
	formatItem = getString,
	singular = "item",
	children,
	...props
}: {
	/** Format a single repeater item or value by taking the string reference and returning a string or React element. */
	formatItem?: (value: T) => string;
	/** Label for one of the repeated thing. */
	singular?: string;
	/** Contents of the popup form. */
	children: React.ReactNode;
} & NamedFieldProps): React.ReactElement {
	const showDialog = useDialog();
	const field = useField<ImmutableDictionary<T>>(props);
	const { name, form, schema, value } = field;
	const itemSchema = getSource<DataSchema<T>>(DataSchema, getSource(DictionarySchema, schema).items);
	useStore(form);
	const { max } = getSource(DictionarySchema, schema);
	const showForm = (k?: string | null) => {
		const value1 = form.data[name] ?? schema.value;
		assertData(value1);
		showDialog(
			<Form<T>
				schema={itemSchema}
				data={k ? itemSchema.validate(value1[k]) : undefined}
				onSubmit={item => {
					const items = form.data[name] ?? {};
					form.publish(name, _rekeyDictionary(withProp(items, k || "newProp", item)));
				}}
			>
				<h2>{k ? `Edit ${singular}` : `Add ${singular}`}</h2>
				{children}
				<SubmitButton>{k ? `Save ${singular}` : `Add ${singular}`}</SubmitButton>
				{k ? (
					<button
						type="button"
						className="button button-confirm"
						onClick={() => {
							const items = form.data[name] ?? {};
							form.publish(name, _rekeyDictionary(omitProps(items, k)));
						}}
						children={`Delete ${singular}`}
					/>
				) : null}
			</Form>,
		);
	};
	assertDictionary(value);
	const entries = getProps(value);
	const length = entries.length;
	return (
		<Field {...field}>
			{entries.map(([k, v]) => (
				<button key={k} type="button" className="input space-hairline" onClick={() => showForm(k)} children={formatItem(v)} />
			))}
			<button type="button" className="input size-small space-none" onClick={() => showForm()} children={`Add ${singular}${max === null ? "" : ` (${length} of ${max})`}`} disabled={max === null ? false : length >= max} />
			<FieldNotice name={name} />
		</Field>
	);
}
